import type React from 'react';
import { message, notification } from 'antd';
import { show, notify, type NotifyKind } from './notify';

/**
 * Re-routes antd's static `message.*` and `notification.*` into notify(), so the ~600 existing
 * call sites share the one top-centre toast channel without each being rewritten. Installed once
 * in main.tsx. antd durations are seconds (0 = sticky); `key` maps to the toast id, so the
 * loading-then-success pattern (`message.loading({ key })` then `message.success({ key })`)
 * still replaces in place.
 */
type MessageArgs = { content?: React.ReactNode; duration?: number; key?: React.Key; type?: string; onClose?: () => void };
type NotificationArgs = { message?: React.ReactNode; description?: React.ReactNode; duration?: number | null; key?: React.Key; type?: string; onClose?: () => void };

const KINDS: NotifyKind[] = ['success', 'error', 'info', 'warning', 'loading'];
const kindOf = (type?: string): NotifyKind => (KINDS.includes(type as NotifyKind) ? (type as NotifyKind) : type === 'warn' ? 'warning' : 'info');
const ms = (seconds?: number | null): number | undefined => (seconds == null ? undefined : seconds === 0 ? 0 : seconds * 1000);

/** antd's MessageType: a close function that is also thenable (resolves when it closes). */
const handle = (id: string, durationMs: number | undefined, onClose?: () => void) => {
  const close = () => notify.dismiss(id);
  const wait = durationMs === 0 ? new Promise<boolean>(() => {}) : new Promise<boolean>((resolve) => setTimeout(() => { onClose?.(); resolve(true); }, durationMs ?? 3000));
  return Object.assign(close, { then: wait.then.bind(wait) });
};

const isArgs = (v: unknown): v is MessageArgs =>
  !!v && typeof v === 'object' && !('$$typeof' in (v as object)) && 'content' in (v as object);

const openMessage = (type: string | undefined, a: unknown, duration?: number | (() => void), onClose?: () => void) => {
  const args: MessageArgs = isArgs(a) ? a : { content: a as React.ReactNode, duration: typeof duration === 'number' ? duration : undefined };
  const close = args.onClose ?? (typeof duration === 'function' ? duration : onClose);
  const durationMs = ms(args.duration);
  const id = show(kindOf(args.type ?? type), args.content, { id: args.key != null ? String(args.key) : undefined, duration: durationMs });
  return handle(id, durationMs, close);
};

const openNotification = (type: string | undefined, args: NotificationArgs = {}) => {
  show(kindOf(args.type ?? type), args.message, {
    id: args.key != null ? String(args.key) : undefined,
    description: args.description,
    duration: args.duration === null ? 0 : ms(args.duration),
  });
};

let installed = false;
export const installNotifyBridge = (): void => {
  if (installed) return;
  installed = true;
  const m = message as any;
  const n = notification as any;
  for (const t of ['success', 'error', 'info', 'warning', 'loading']) {
    m[t] = (a: unknown, d?: number | (() => void), c?: () => void) => openMessage(t, a, d, c);
  }
  m.warn = m.warning;
  m.open = (a: MessageArgs) => openMessage(a?.type, a);
  m.destroy = (key?: React.Key) => notify.dismiss(key != null ? String(key) : undefined);
  for (const t of ['success', 'error', 'info', 'warning']) {
    n[t] = (a: NotificationArgs) => openNotification(t, a);
  }
  n.warn = n.warning;
  n.open = (a: NotificationArgs) => openNotification(a?.type, a);
  n.destroy = (key?: React.Key) => notify.dismiss(key != null ? String(key) : undefined);
};
